// ============================================
// OrderFlowChart — B-Book vs Execution Report order landings, live
//
// Two synchronized stacked panels on one time axis, styled after the
// Portfolio A/B/C Combination chart:
//   • Top    — B-Book: client orders landing in the B-Book (MT5 + cTrader
//              MASTER book B). Opens, closes and volume changes of B-Book
//              positions, in lots.
//   • Bottom — Execution Report: orders sent to the LPs (the blotter rows),
//              in units.
// A right-hand column totals each panel over the selected window.
//
// Data:
//   • B-Book — its own socket on mt5.position + ctrader.position (the same
//     feed as the B-Book page). REST seed for MT5 positions and the cTrader
//     MASTER, then events only. A live landing is stamped on arrival; a seeded
//     one at the position's open time.
//   • Execution Report — read from the page's live row map once a second
//     (getRows), so it follows the WebSocket without waiting for the grid sync.
//
// Nothing is polled: the 1s tick only re-buckets what is already held.
// ============================================

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

import {
  mt5Api,
  ctraderApi,
  ctraderDataApi,
  connectBBookWebSocket,
  type BBookWsEvent,
  type CTraderPositionMessage,
  type CTraderPositionRow,
  type MT5PositionWithNode,
} from '@/services/api';
import { BOOK_COLORS } from '@/components/portfolio/charts/bookColors';
import type { ExecutionReportRow } from '@/pages/ExecutionReport';

// ── Constants ──────────────────────────────────────────────────
const COLOR_B  = BOOK_COLORS.b;
const COLOR_ER = BOOK_COLORS.a;
const DAY_MS   = 86_400_000;
const MONO     = 'IBM Plex Mono, monospace';

const FILLED_STATES   = new Set(['FILLED', 'HEDGED', 'PARTIAL', 'CLOSED', 'CLOSING']);
const REJECTED_STATES = new Set(['REJECTED', 'FAILED', 'ERROR']);

type WindowKey = '5M' | '15M' | '1H' | '4H' | 'TODAY';
const WINDOWS: Record<WindowKey, { label: string; span: number | null; bucket: number }> = {
  '5M':    { label: '5M',    span: 5 * 60_000,       bucket: 5_000 },
  '15M':   { label: '15M',   span: 15 * 60_000,      bucket: 15_000 },
  '1H':    { label: '1H',    span: 60 * 60_000,      bucket: 60_000 },
  '4H':    { label: '4H',    span: 4 * 60 * 60_000,  bucket: 5 * 60_000 },
  'TODAY': { label: 'Today', span: null,             bucket: 5 * 60_000 },
};
type Metric = 'volume' | 'orders';

// ── Helpers ────────────────────────────────────────────────────
const pad2 = (n: number) => String(n).padStart(2, '0');
const startOfUtcDay = (ms: number) => Math.floor(ms / DAY_MS) * DAY_MS;

function fmtTime(ts: number, withSeconds: boolean): string {
  const d = new Date(ts);
  const hm = `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`;
  return withSeconds ? `${hm}:${pad2(d.getUTCSeconds())}` : hm;
}

function fmtCompact(v: number): string {
  const a = Math.abs(v);
  if (a >= 1e9) return `${+(v / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `${+(v / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${+(v / 1e3).toFixed(1)}k`;
  return `${+v.toFixed(2)}`;
}

function fmtAgo(ts: number | null, now: number): string {
  if (ts == null) return '—';
  const s = Math.max(0, Math.round((now - ts) / 1000));
  if (s < 60)   return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return fmtTime(ts, false);
}

// FIX format: YYYYMMDD-HH:MM:SS.mmm (UTC)
function parseFixTime(ts: string): number {
  const m = ts.match(/^(\d{4})(\d{2})(\d{2})-(\d{2}):(\d{2}):(\d{2})\.(\d{3})/);
  if (!m) return 0;
  return Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6], +m[7]);
}

// ══════════════════════════════════════════════════════════════
// B-Book landing feed
// ══════════════════════════════════════════════════════════════
type LandingKind = 'open' | 'close' | 'change';
interface Landing { ts: number; vol: number; kind: LandingKind }
type FeedStatus = 'connecting' | 'live' | 'reconnecting';

interface HeldPosition { vol: number; book: string }

function useBBookLandings() {
  const landingsRef = useRef<Landing[]>([]);
  const [status, setStatus] = useState<FeedStatus>('connecting');

  useEffect(() => {
    let alive = true;
    let retry = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let stop: (() => void) | null = null;

    // key → held position. MT5: "m:<pid>", cTrader MASTER: "c:<pid>".
    const held     = new Map<string, HeldPosition>();
    const counted  = new Set<string>();          // opens already plotted
    let mt5Known   = false;                      // MT5 baseline exists
    let ctMasterId: number | null = null;
    let ctKnown    = false;

    const push = (ts: number, vol: number, kind: LandingKind) => {
      if (!(vol > 0)) return;
      const list = landingsRef.current;
      list.push({ ts, vol, kind });
      // Keep one UTC day plus the longest rolling window.
      const cutoff = Math.min(startOfUtcDay(Date.now()), Date.now() - WINDOWS['4H'].span!);
      if (list.length > 4096 && list[0].ts < cutoff) {
        landingsRef.current = list.filter(l => l.ts >= cutoff);
      }
    };

    // A position seen in a full set. Opened today and not counted yet → an
    // open landing at its open time; otherwise baseline only.
    const seen = (key: string, vol: number, book: string, timeCreateSec: number) => {
      held.set(key, { vol, book });
      if (counted.has(key)) return;
      counted.add(key);
      const ts = timeCreateSec * 1000;
      if (book === 'B' && ts >= startOfUtcDay(Date.now())) push(ts, vol, 'open');
    };

    // A full set for one source: anything held and now absent was closed.
    const reconcile = (prefix: string, incoming: Map<string, { vol: number; book: string; t: number }>, known: boolean) => {
      const now = Date.now();
      if (known) {
        for (const [key, h] of held) {
          if (!key.startsWith(prefix) || incoming.has(key)) continue;
          if (h.book === 'B') push(now, h.vol, 'close');
          held.delete(key);
        }
      } else {
        for (const key of [...held.keys()]) if (key.startsWith(prefix) && !incoming.has(key)) held.delete(key);
      }
      for (const [key, p] of incoming) {
        if (known && !counted.has(key) && p.book === 'B') {
          // New since the last full set, e.g. opened while the socket was down.
          counted.add(key);
          held.set(key, { vol: p.vol, book: p.book });
          push(Math.max(p.t * 1000, startOfUtcDay(now)), p.vol, 'open');
        } else {
          seen(key, p.vol, p.book, p.t);
        }
      }
    };

    const mt5Full = (list: MT5PositionWithNode[]) => {
      const m = new Map<string, { vol: number; book: string; t: number }>();
      for (const p of list) m.set(`m:${p.position_id}`, { vol: p.volume_lots ?? 0, book: 'B', t: p.time_create });
      reconcile('m:', m, mt5Known);
      mt5Known = true;
    };

    const ctFull = (rows: CTraderPositionRow[]) => {
      const m = new Map<string, { vol: number; book: string; t: number }>();
      for (const r of rows) {
        if (r.node_id !== ctMasterId) continue;
        m.set(`c:${r.position_id}`, { vol: r.volume_lots ?? 0, book: r.book, t: r.time_create });
      }
      reconcile('c:', m, ctKnown);
      ctKnown = true;
    };

    // One position event: open, close or a volume change of a held position.
    const apply = (key: string, type: string, vol: number | undefined, book: string | undefined) => {
      const now  = Date.now();
      const prev = held.get(key);
      if (type === 'POSITION_DELETE') {
        if (prev && prev.book === 'B') push(now, prev.vol, 'close');
        held.delete(key);
        return;
      }
      if (type === 'POSITION_ADD' && !prev) {
        const v = vol ?? 0;
        const b = book ?? 'B';
        held.set(key, { vol: v, book: b });
        if (!counted.has(key)) {
          counted.add(key);
          if (b === 'B') push(now, v, 'open');
        }
        return;
      }
      if (!prev) return;   // unknown position — wait for a full set
      const nextBook = book ?? prev.book;
      const nextVol  = vol ?? prev.vol;
      // A move between books is a reclassification, not an order.
      if (prev.book === 'B' && nextBook === 'B' && nextVol !== prev.vol) {
        push(now, Math.abs(nextVol - prev.vol), 'change');
      }
      held.set(key, { vol: nextVol, book: nextBook });
    };

    const onMt5 = (ev: BBookWsEvent) => {
      if (ev.type === 'SNAPSHOT') { mt5Full(ev.data as MT5PositionWithNode[]); return; }
      if (ev.type === 'POSITION_ADD' || ev.type === 'POSITION_CHANGE') {
        const d = ev.data as Partial<MT5PositionWithNode> & { position_id: number };
        apply(`m:${d.position_id}`, ev.type, d.volume_lots, 'B');
        return;
      }
      if (ev.type === 'POSITION_DELETE') {
        apply(`m:${(ev.data as { position_id: number }).position_id}`, ev.type, undefined, undefined);
      }
      // POSITION_BATCH carries prices only — ignored.
    };

    const onCt = (msg: CTraderPositionMessage) => {
      if (msg.type === 'SNAPSHOT') {
        const master = msg.data.nodes?.find(n => n.node_type === 'MASTER');
        if (master) ctMasterId = master.node_id;
        ctFull(msg.data.positions ?? []);
        return;
      }
      if (msg.type === 'POSITION_SYNC') {
        if (msg.data.node_id === ctMasterId) ctFull(msg.data.positions ?? []);
        return;
      }
      if (msg.type === 'POSITION_PRICES') return;
      const r = msg.data;
      if (r.node_id !== ctMasterId) return;
      apply(`c:${r.position_id}`, msg.type, r.volume_lots ?? undefined, r.book);
    };

    const connect = () => {
      if (!alive) return;
      stop = connectBBookWebSocket(
        onMt5,
        (s) => {
          if (!alive) return;
          if (s === 'open') { retry = 0; setStatus('live'); return; }
          setStatus('reconnecting');
          const delay = Math.min(1000 * 2 ** retry, 30_000);
          retry += 1;
          timer = setTimeout(connect, delay);
        },
        onCt,
      );
    };

    // Seeds: MT5 positions and the cTrader MASTER's book-B positions. A full
    // set from the socket that arrived first makes the seed redundant.
    (async () => {
      try {
        const { positions } = await mt5Api.getAllBBookPositions();
        if (alive && !mt5Known) mt5Full(positions);
      } catch { /* the socket snapshot fills in */ }
    })();
    (async () => {
      try {
        const res = await ctraderApi.getMaster();
        const master = res.master;
        if (!alive || !master || ctMasterId != null) return;
        ctMasterId = master.node_id;
        const rows: CTraderPositionRow[] = [];
        for (let offset = 0; ;) {
          const page = await ctraderDataApi.getPositions(master.node_id, { book: 'B', limit: 1000, offset });
          if (!alive || ctKnown) return;
          for (const p of page.positions ?? []) rows.push({ ...p, platform: 'CTRADER', node_id: master.node_id, node_name: master.node_name });
          offset += page.returned;
          if (!page.returned || offset >= page.total) break;
        }
        if (alive && !ctKnown && ctMasterId === master.node_id) ctFull(rows);
      } catch { /* no cTrader MASTER, or it is not started */ }
    })();

    connect();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
      stop?.();
    };
  }, []);

  return { landingsRef, status };
}

// ══════════════════════════════════════════════════════════════
// Component
// ══════════════════════════════════════════════════════════════
interface Point {
  ts:          number;
  b_vol:       number;
  b_orders:    number;
  b_opens:     number;
  b_closes:    number;
  e_vol:       number;
  e_orders:    number;
  e_filled:    number;
  e_rejected:  number;
}

interface Props {
  /** Live Execution Report rows (already filtered to the selected LP). */
  getRows: () => ExecutionReportRow[];
  lpLabel: string;
  onClose: () => void;
}

export function OrderFlowChart({ getRows, lpLabel, onClose }: Props) {
  const { landingsRef, status } = useBBookLandings();
  const [windowKey, setWindowKey] = useState<WindowKey>('15M');
  const [metric,    setMetric]    = useState<Metric>('volume');
  const [now,       setNow]       = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const { points, totals } = useMemo(() => {
    const w = WINDOWS[windowKey];
    const bucket = w.bucket;
    const last   = Math.floor(now / bucket) * bucket;
    const from   = w.span == null ? startOfUtcDay(now) : last - w.span + bucket;
    const n      = Math.max(1, Math.floor((last - from) / bucket) + 1);

    const pts: Point[] = Array.from({ length: n }, (_, i) => ({
      ts: from + i * bucket,
      b_vol: 0, b_orders: 0, b_opens: 0, b_closes: 0,
      e_vol: 0, e_orders: 0, e_filled: 0, e_rejected: 0,
    }));
    const idx = (ts: number) => {
      if (ts < from || ts >= last + bucket) return -1;
      return Math.floor((ts - from) / bucket);
    };

    const t = {
      b_vol: 0, b_orders: 0, b_opens: 0, b_closes: 0, b_last: null as number | null,
      e_vol: 0, e_orders: 0, e_filled: 0, e_rejected: 0, e_last: null as number | null,
    };

    for (const l of landingsRef.current) {
      const i = idx(l.ts);
      if (i < 0) continue;
      const p = pts[i];
      p.b_vol += l.vol; p.b_orders += 1;
      if (l.kind === 'open')  p.b_opens  += 1;
      if (l.kind === 'close') p.b_closes += 1;
      t.b_vol += l.vol; t.b_orders += 1;
      if (l.kind === 'open')  t.b_opens  += 1;
      if (l.kind === 'close') t.b_closes += 1;
      if (t.b_last == null || l.ts > t.b_last) t.b_last = l.ts;
    }

    for (const r of getRows()) {
      const ts = parseFixTime(r.transact_time);
      const i  = idx(ts);
      if (i < 0) continue;
      const vol = r.order_qty > 0 ? r.order_qty : r.fill_qty;
      const p = pts[i];
      p.e_vol += vol; p.e_orders += 1;
      t.e_vol += vol; t.e_orders += 1;
      if (FILLED_STATES.has(r.te_status))   { p.e_filled   += 1; t.e_filled   += 1; }
      if (REJECTED_STATES.has(r.te_status)) { p.e_rejected += 1; t.e_rejected += 1; }
      if (t.e_last == null || ts > t.e_last) t.e_last = ts;
    }

    return { points: pts, totals: t };
  }, [now, windowKey, getRows, landingsRef]);

  const withSeconds = WINDOWS[windowKey].bucket < 60_000;
  const bKey = metric === 'volume' ? 'b_vol' : 'b_orders';
  const eKey = metric === 'volume' ? 'e_vol' : 'e_orders';
  const statusColor = status === 'live' ? '#6aaa78' : status === 'reconnecting' ? '#c09060' : '#c09060';
  const statusLabel = status === 'live' ? 'LIVE' : status === 'reconnecting' ? 'RECONNECTING' : 'CONNECTING';

  return (
    <div className="h-full w-full flex flex-col" style={{ backgroundColor: '#1a1a1c' }}>
      {/* Header */}
      <div className="px-4 py-2 border-b border-[#505050] flex items-center justify-between gap-4 flex-shrink-0" style={{ backgroundColor: '#2a292c' }}>
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-white">Order Flow</h3>
          <p className="text-xs text-[#ccc]">
            Orders landing in the B-Book and orders sent to {lpLabel} · UTC
          </p>
        </div>
        <div className="flex items-center gap-4 text-xs">
          <Segmented
            options={(Object.keys(WINDOWS) as WindowKey[]).map(k => ({ key: k, label: WINDOWS[k].label }))}
            value={windowKey}
            onChange={setWindowKey}
          />
          <Segmented
            options={[{ key: 'volume', label: 'Volume' }, { key: 'orders', label: 'Orders' }]}
            value={metric}
            onChange={setMetric}
          />
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: statusColor }} />
            <span className="font-mono text-[10px]" style={{ color: statusColor }}>{statusLabel}</span>
          </div>
          <button
            onClick={onClose}
            className="px-3 py-1 text-xs text-white bg-[#232225] border border-[#808080] rounded hover:bg-[#3a3a3c] transition-colors"
          >
            Close
          </button>
        </div>
      </div>

      {/* Body: stacked panels + totals column */}
      <div className="flex-1 min-h-0 flex">
        <div className="flex-1 min-w-0 flex flex-col gap-1 p-2">
          <div className="flex-1 min-h-0">
            <FlowPanel
              data={points}
              dataKey={bKey}
              name="B-Book"
              unit={metric === 'volume' ? 'lots' : 'orders'}
              color={COLOR_B}
              showXAxis={false}
              withSeconds={withSeconds}
            />
          </div>
          <div className="flex-1 min-h-0">
            <FlowPanel
              data={points}
              dataKey={eKey}
              name="Execution Report"
              unit={metric === 'volume' ? 'units' : 'orders'}
              color={COLOR_ER}
              showXAxis
              withSeconds={withSeconds}
            />
          </div>
        </div>

        <div className="w-[200px] flex-shrink-0 border-l border-[#505050] flex flex-col" style={{ backgroundColor: '#232225' }}>
          <TotalsBlock
            title="B-Book"
            color={COLOR_B}
            rows={[
              ['Orders', String(totals.b_orders)],
              ['Volume', `${fmtCompact(totals.b_vol)} lots`],
              ['Opens',  String(totals.b_opens)],
              ['Closes', String(totals.b_closes)],
              ['Last',   fmtAgo(totals.b_last, now)],
            ]}
          />
          <div className="border-t border-[#505050]" />
          <TotalsBlock
            title="Execution Report"
            color={COLOR_ER}
            rows={[
              ['Orders',   String(totals.e_orders)],
              ['Volume',   `${fmtCompact(totals.e_vol)} units`],
              ['Filled',   String(totals.e_filled)],
              ['Rejected', String(totals.e_rejected)],
              ['Last',     fmtAgo(totals.e_last, now)],
            ]}
          />
        </div>
      </div>
    </div>
  );
}

// ── FlowPanel — one of the two stacked charts ──────────────────
function FlowPanel({
  data, dataKey, name, unit, color, showXAxis, withSeconds,
}: {
  data:        Point[];
  dataKey:     keyof Point;
  name:        string;
  unit:        string;
  color:       string;
  showXAxis:   boolean;
  withSeconds: boolean;
}) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart
        data={data}
        syncId="order-flow"
        margin={{ top: 8, right: 32, bottom: showXAxis ? 4 : 0, left: 8 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#3a3a3c" />
        <XAxis
          dataKey="ts"
          type="number"
          scale="time"
          domain={['dataMin', 'dataMax']}
          tickFormatter={(v: number) => fmtTime(v, withSeconds)}
          stroke="#808080"
          tick={showXAxis ? { fill: '#e0e0e0', fontSize: 11, fontFamily: MONO } : false}
          axisLine={showXAxis}
          tickLine={showXAxis}
          height={showXAxis ? 22 : 0}
          minTickGap={40}
        />
        <YAxis
          tickFormatter={fmtCompact}
          stroke="#808080"
          tick={{ fill: '#e0e0e0', fontSize: 10, fontFamily: MONO }}
          width={64}
          allowDecimals={unit !== 'orders'}
          domain={[0, 'auto']}
          label={{
            value: name,
            angle: -90,
            position: 'insideLeft',
            offset: 0,
            style: { fill: color, fontSize: 11, fontWeight: 600, textAnchor: 'middle' },
          }}
        />
        <Tooltip
          content={<FlowTooltip name={name} unit={unit} withSeconds={withSeconds} />}
          cursor={{ stroke: '#ffffff66', strokeWidth: 1 }}
        />
        <Line
          type="monotone"
          dataKey={dataKey}
          stroke={color}
          strokeWidth={2}
          dot={false}
          isAnimationActive={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

function FlowTooltip({
  active, payload, name, unit, withSeconds,
}: {
  active?: boolean;
  payload?: Array<{ value: number; payload: Point }>;
  name: string;
  unit: string;
  withSeconds: boolean;
}) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  const isB = name === 'B-Book';
  return (
    <div className="rounded px-2 py-1 text-xs" style={{ backgroundColor: '#2a292c', border: '1px solid #505050' }}>
      <p className="text-white">{name} · <span className="font-mono">{fmtTime(p.ts, withSeconds)}</span></p>
      <p className="text-white">
        <span className="font-mono font-semibold">{fmtCompact(payload[0].value)}</span> {unit}
      </p>
      {isB ? (
        <p className="text-[#ddd]">
          Opens <span className="font-mono">{p.b_opens}</span> · Closes <span className="font-mono">{p.b_closes}</span>
        </p>
      ) : (
        <p className="text-[#ddd]">
          Filled <span className="font-mono">{p.e_filled}</span> · Rejected <span className="font-mono">{p.e_rejected}</span>
        </p>
      )}
    </div>
  );
}

function TotalsBlock({ title, color, rows }: { title: string; color: string; rows: [string, string][] }) {
  return (
    <div className="px-3 py-3">
      <p className="text-xs font-semibold mb-2" style={{ color }}>{title}</p>
      {rows.map(([k, v]) => (
        <div key={k} className="flex items-center justify-between py-0.5">
          <span className="text-[11px] text-[#ddd] uppercase tracking-wider">{k}</span>
          <span className="font-mono text-white" style={{ fontSize: 12 }}>{v}</span>
        </div>
      ))}
    </div>
  );
}

function Segmented<K extends string>({
  options, value, onChange,
}: {
  options: { key: K; label: string }[];
  value: K;
  onChange: (k: K) => void;
}) {
  return (
    <div className="flex items-center rounded border border-[#606060] overflow-hidden">
      {options.map(o => (
        <button
          key={o.key}
          onClick={() => onChange(o.key)}
          className="px-2 py-1 text-xs transition-colors"
          style={{
            backgroundColor: value === o.key ? '#49b3b3' : '#232225',
            color: value === o.key ? '#1a1a1c' : '#fff',
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
